import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BASE_URL_TOKEN, DeviceRegistrationHistory } from '@raptor-fe/core';
import { Observable } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class DeviceRegistrationHistoryService {
    protected http = inject(HttpClient);
    protected baseUrl = inject(BASE_URL_TOKEN);

    /**
     * Retrieves the device registration history for a specific user device within a given tenant.
     *
     * @param {string} tenantId - The unique identifier of the tenant.
     * @param {string} userDeviceId - The unique identifier of the user device.
     * @param {number} [page=1] - The page number for pagination, default is 1.
     * @param {number} [pageSize=25] - The number of items per page, default is 25.
     * @returns {Observable<DeviceRegistrationHistory[]>} - An observable that emits an array of device registration history records.
     */
    getByUserDeviceId(tenantId: string, userDeviceId: string, page = 1, pageSize = 25): Observable<DeviceRegistrationHistory[]> {
        const params = new HttpParams().set('page', page.toString()).set('page-size', pageSize.toString());

        return this.http.get<DeviceRegistrationHistory[]>(`${this.baseUrl}v1/tenant/${tenantId}/UserDevice/id/${userDeviceId}/history`, { params });
    }

    /**
     * Fetches the device registration history of a specific device based on its device ID.
     *
     * @param {string} tenantId - The unique identifier of the tenant.
     * @param {string} deviceId - The unique identifier of the device.
     * @param {number} [page=1] - The page number for the paginated results.
     * @param {number} [pageSize=25] - The number of items to include per page in the results.
     * @return {Observable<DeviceRegistrationHistory[]>} An observable containing an array of device registration history entries.
     */
    getByDeviceId(tenantId: string, deviceId: string, page = 1, pageSize = 25): Observable<DeviceRegistrationHistory[]> {
        const params = new HttpParams().set('page', page.toString()).set('page-size', pageSize.toString());

        return this.http.get<DeviceRegistrationHistory[]>(`${this.baseUrl}v1/tenant/${tenantId}/UserDevice/history/device-id/${deviceId}`, {
            params,
        });
    }

    /**
     * Fetches the device registration history for a specified user within a tenant.
     *
     * @param {string} tenantId - The unique identifier for the tenant.
     * @param {string} userId - The unique identifier for the user whose device registration history is to be retrieved.
     * @param {number} [page=1] - The page number for paginated results (optional, defaults to 1).
     * @param {number} [pageSize=25] - The number of records to be fetched per page (optional, defaults to 25).
     * @return {Observable<DeviceRegistrationHistory[]>} An observable emitting an array of device registration history records.
     */
    getByUserId(tenantId: string, userId: string, page = 1, pageSize = 25): Observable<DeviceRegistrationHistory[]> {
        const params = new HttpParams().set('page', page.toString()).set('page-size', pageSize.toString());

        return this.http.get<DeviceRegistrationHistory[]>(`${this.baseUrl}v1/tenant/${tenantId}/UserDevice/history/user-id/${userId}`, { params });
    }
}
